/**
 * ExtraFields Component
 *
 * Purpose: Renders all view fields that have NOT been explicitly placed by a
 *          <Field> component, providing automatic coverage of new/unplaced fields.
 *          In Maestro mode (appliedFieldKeys provided), applies border-color
 *          indicators via FieldIndicatorWrapper (ORCH-251) and associates the
 *          indicator status text with each input via aria-describedby (ORCH-237).
 * Architecture: Reads the field registry and placed fields set from context,
 *               filters to unplaced+visible fields, sorts by display_order,
 *               and renders each using getFieldRenderer().
 * Dependencies: React (useMemo), MUI (Box), TemplateFormContext (useTemplateFormContext),
 *               metadata-form/FieldRenderers (getFieldRenderer),
 *               ./FieldIndicatorIcon (getFieldIndicatorState, valuePresent),
 *               ./FieldIndicatorWrapper, ./types (ExtraFieldsProps)
 */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useTemplateFormContext } from './TemplateFormContext';
import { getFieldRenderer } from '../metadata-form/FieldRenderers';
import {
  getFieldIndicatorState,
  valuePresent,
  INDICATOR_BORDER,
  NO_INDICATOR,
} from './FieldIndicatorIcon';
import FieldIndicatorWrapper, { indicatorDescriptorId } from './FieldIndicatorWrapper';
import type { ExtraFieldsProps } from './types';
import { fieldGridSx, fieldSpanAllSx, spansAllColumns } from '../form-layout/fieldGrid';

/**
 * ExtraFields — renders all unplaced view fields in a grid layout
 *
 * Purpose: Catches any fields not explicitly placed by <Field> components,
 *          ensuring full form coverage as metadata evolves.
 * Logic: 1) Gets all fields from registry
 *        2) Filters out placed fields, excluded IDs, and invisible fields
 *        3) Sorts by display_order
 *        4) Renders each in a responsive grid using getFieldRenderer(datatype)
 *        5) In Maestro mode, wraps in FieldIndicatorWrapper (border-color +
 *           accessible description) and injects aria-describedby into inputProps
 * Input: ExtraFieldsProps — columns (1|2), exclude (fieldDefIds to skip)
 * Output: Grid of rendered field components, or null if no extra fields remain
 */
const ExtraFields: React.FC<ExtraFieldsProps> = ({ columns = 1, exclude = [] }) => {
  const {
    fieldRegistry,
    placedFields,
    values,
    errors,
    touched,
    actions,
    entityId,
    readOnly,
    indicatorMode,
    appliedFieldKeys,
    fieldResults,
  } = useTemplateFormContext();

  const extraFields = useMemo(() => {
    return Array.from(fieldRegistry.values())
      .filter(
        (f) =>
          !placedFields.has(f.fielddef_id) &&
          !exclude.includes(f.fielddef_id) &&
          f.is_visible !== false
      )
      .sort((a, b) => a.display_order - b.display_order);
  }, [fieldRegistry, placedFields, exclude]);

  if (extraFields.length === 0) return null;

  const isMaestroMode = appliedFieldKeys !== undefined;

  return (
    <Box sx={{ ...fieldGridSx(columns), ...fieldSpanAllSx }}>
      {extraFields.map((field) => {
        const Renderer = getFieldRenderer(field.field_definition.datatype);
        const id = field.fielddef_id;
        const isRequired = field.is_required ?? false;

        const indicator = isMaestroMode
          ? getFieldIndicatorState({
              fieldId: id,
              isRequired,
              indicatorMode,
              appliedFieldKeys,
              fieldResults,
              hasValue: valuePresent(values[id]),
            })
          : NO_INDICATOR;

        const borderColor = indicator.state ? INDICATOR_BORDER[indicator.state] : undefined;

        const rendererEl = (
          <Renderer
            field={field}
            value={values[id]}
            onChange={(value: any) => actions.setValue(id, value)}
            error={errors[id]}
            touched={touched[id]}
            disabled={readOnly}
            entityId={entityId}
            indicatorBorderColor={borderColor}
            {...(isMaestroMode
              ? { inputProps: { 'aria-describedby': indicatorDescriptorId(id) } }
              : {})}
          />
        );

        return (
          <Box key={id} sx={spansAllColumns(field.field_definition) ? fieldSpanAllSx : undefined}>
            {isMaestroMode ? (
              <FieldIndicatorWrapper fieldId={id} indicator={indicator}>
                {rendererEl}
              </FieldIndicatorWrapper>
            ) : (
              rendererEl
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ExtraFields;
